import { apiClient } from './client'
import type {
  OAuthAppSummary,
  OAuthAppsPagedResponse,
  OAuthAppDetail,
  AcknowledgeOAuthAppRequest,
  ConsentRecordRow,
  PermissionHeatmap,
} from '@/components/SecurityCenter/appPermissionsTypes'

/**
 * OAuth app permissions / App Posture surface. Split out of `lib/api.ts`
 * (WO-10.4).
 */

/**
 * Get summary of OAuth app risk distribution for a tenant
 */
export async function getOAuthAppSummary(tenantId: string): Promise<OAuthAppSummary> {
  const response = await apiClient.get(`/oauth-apps/tenant/${tenantId}/summary`)
  return response.data?.data ?? response.data
}

/**
 * Get paginated list of OAuth apps with optional filtering
 */
export async function getOAuthApps(
  tenantId: string,
  page = 1,
  pageSize = 20,
  riskTier?: string,
  appType?: string,
  status?: string,
  verified?: string,
  search?: string
): Promise<OAuthAppsPagedResponse> {
  const response = await apiClient.get(`/oauth-apps/tenant/${tenantId}`, {
    params: { page, pageSize, riskTier, appType, status, verified, search }
  })
  return response.data?.data ?? response.data
}

/**
 * Get detailed information for a specific OAuth app including permissions
 */
export async function getOAuthAppDetail(tenantId: string, id: string): Promise<OAuthAppDetail> {
  const response = await apiClient.get(`/oauth-apps/tenant/${tenantId}/${id}`)
  return response.data?.data ?? response.data
}

/**
 * Acknowledge or flag an OAuth app with a reason and optional expiry
 */
export async function acknowledgeOAuthApp(tenantId: string, appId: string, request: AcknowledgeOAuthAppRequest): Promise<OAuthAppDetail> {
  const response = await apiClient.put(`/oauth-apps/tenant/${tenantId}/${appId}/acknowledge`, request)
  return response.data?.data ?? response.data
}

/**
 * Trigger a sync of OAuth apps from Microsoft Graph for a tenant
 */
export async function syncOAuthApps(tenantId: string): Promise<void> {
  await apiClient.post(`/oauth-apps/tenant/${tenantId}/sync`)
}

/**
 * Recent OAuth-consent feed for the App Posture page (§8.5).
 * Returns up to `limit` rows ordered by GrantedAt DESC.
 */
export async function getOAuthRecentConsents(
  tenantId: string,
  limit = 10,
): Promise<ConsentRecordRow[]> {
  const response = await apiClient.get(`/oauth-apps/recent-consents`, {
    params: { tenantId, limit },
  })
  const payload = response.data?.data ?? response.data
  return Array.isArray(payload) ? (payload as ConsentRecordRow[]) : []
}

/**
 * Operation x Scope permission heatmap for the App Posture page (§8.3).
 * Server-side aggregation over the tenant's classified OAuth permissions.
 */
export async function getOAuthPermissionHeatmap(
  tenantId: string,
): Promise<PermissionHeatmap> {
  const response = await apiClient.get(`/oauth-apps/permission-heatmap`, {
    params: { tenantId },
  })
  return response.data?.data ?? response.data
}

export const oauthApi = {
  getOAuthAppSummary,
  getOAuthApps,
  getOAuthAppDetail,
  acknowledgeOAuthApp,
  syncOAuthApps,
  getOAuthRecentConsents,
  getOAuthPermissionHeatmap,
}
